import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, Globe } from "lucide-react";
import api from "../../lib/api";

export default function AdminLogin() {
  const { adminLogin, changePassword } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [serverIp, setServerIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  useEffect(() => {
    api.get("/server-ip").then(r => setServerIp(r.data.ip)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await adminLogin(username, password);
      if (result.must_change_password) {
        setMustChange(true);
        setCurrentPw(password);
        toast.info("Please change your password before continuing");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success("Password changed successfully");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-login-page">
      <div className="w-full max-w-sm border border-gray-200 bg-white p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-black flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">Minibar POS</h1>
            <p className="text-xs text-gray-500 uppercase tracking-[0.15em] font-semibold">Admin Portal</p>
          </div>
        </div>

        {!mustChange ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Username</Label>
              <Input
                data-testid="admin-username-input"
                value={username} onChange={(e) => setUsername(e.target.value)}
                className="mt-1 rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Password</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="admin-password-input"
                  type={showPw ? "text" : "password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black pr-10"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button data-testid="admin-login-button" type="submit" disabled={loading}
              className="w-full rounded-none bg-black hover:bg-gray-800 text-white font-semibold h-11">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <p className="text-sm text-gray-600 mb-2">You must change your password before continuing.</p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">New Password</Label>
              <Input data-testid="new-password-input" type="password" value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="mt-1 rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Confirm Password</Label>
              <Input data-testid="confirm-password-input" type="password" value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="mt-1 rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black" />
            </div>
            <p className="text-xs text-gray-400">Min 12 chars, upper, lower, digit, special char</p>
            <Button data-testid="change-password-button" type="submit" disabled={loading}
              className="w-full rounded-none bg-black hover:bg-gray-800 text-white font-semibold h-11">
              {loading ? "Updating..." : "Change Password & Continue"}
            </Button>
          </form>
        )}

        <div className="mt-6 flex justify-between">
          <a href="/admin/reset-password" className="text-xs text-gray-400 hover:text-gray-600 uppercase tracking-[0.1em]">
            Forgot Password?
          </a>
          <a href="/pos/login" className="text-xs text-gray-400 hover:text-gray-600 uppercase tracking-[0.1em]">
            POS Terminal Login
          </a>
        </div>
        {serverIp && (
          <div className="mt-4 flex items-center justify-center gap-1 text-[10px] text-gray-300">
            <Globe className="w-3 h-3" /> Server IP: {serverIp}
          </div>
        )}
      </div>
    </div>
  );
}
